import { useEffect, useState, useSyncExternalStore } from 'react';
import { useParams } from 'react-router-dom';
import { sessionStorage, STORAGE_KEYS } from '../utils/sessionStorage.js';
import {
  getStoredToken,
  msUntilExpiry,
  refreshToken,
  subscribeAuthStatus,
  getAuthStatusSnapshot,
} from '../api/auth.js';
import LinkExpired from './LinkExpired.jsx';

// Re-render when the token is refused and whenever it changes (a refresh).
const authSnapshot = () => `${getAuthStatusSnapshot() ?? ''}\n${getStoredToken() ?? ''}`;

export default function AdminAuthGate({ children, requiredPermission }) {
  const { id } = useParams();
  useSyncExternalStore(subscribeAuthStatus, authSnapshot);
  const identity = sessionStorage.get(STORAGE_KEYS.AUTH_IDENTITY);
  const token = getStoredToken();
  // The API refused this token - final (see auth.js getAuthStatusSnapshot).
  const refused = !!getAuthStatusSnapshot();
  // Past its expiry by its clock. For the link's token that clock is the device's, which
  // may run fast - so this is not final: one refresh asks the server, and its answer
  // decides (a new token re-renders the page in; a 401 marks the token refused).
  const looksExpired = !!token && !refused && (msUntilExpiry(token) ?? 1) <= 0;
  const [check, setCheck] = useState({ attempt: 0, failedFor: null });

  useEffect(() => {
    if (!looksExpired) return undefined;
    let live = true;
    refreshToken().catch((e) => {
      if (live && e?.status !== 401) setCheck((c) => ({ ...c, failedFor: token }));
    });
    return () => {
      live = false;
    };
  }, [looksExpired, token, check.attempt]);

  // AuthGate always stores the identity and its token together: one without the other
  // is no access link at all.
  if (!identity || !token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="font-semibold text-gray-800">Access denied</p>
        <p className="max-w-sm text-sm text-gray-500">This page requires a valid admin access link.</p>
      </div>
    );
  }

  if (id && String(identity.sessionId) !== String(id)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="font-semibold text-gray-800">Access denied</p>
        <p className="max-w-sm text-sm text-gray-500">Your admin link is for a different session. Open this session's own admin link to continue.</p>
      </div>
    );
  }

  // The token is dead: say the link expired instead of letting the page fail call by call
  // into "We couldn't load your details".
  if (refused) {
    return <LinkExpired />;
  }

  if (looksExpired) {
    if (check.failedFor === token) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-6 text-center">
          <p className="font-semibold text-gray-800">We couldn't check your link</p>
          <p className="max-w-sm text-sm text-gray-500">Please check your connection and try again.</p>
          <button
            onClick={() => setCheck((c) => ({ attempt: c.attempt + 1, failedFor: null }))}
            className="mt-4 px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
        Verifying your access link…
      </div>
    );
  }

  if (requiredPermission && identity.permission?.[requiredPermission] !== true) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="font-semibold text-gray-800">Access restricted</p>
        <p className="max-w-sm text-sm text-gray-500">Your admin access doesn't include permission for this section.</p>
      </div>
    );
  }

  return children;
}
